/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { formatters } from '@hcecbp/i18n-utils';
import { Legend } from '@hcecbp/legend-component';
import { Chart } from '@scuf/charts';
import { ResizerProps } from '@scuf/charts/dist/types/ResizerProps';
import { useTranslation } from '@scuf/localization';
import { Axis, AxisTickPositionerCallbackFunction } from 'highcharts';
import moment from 'moment';
import React from 'react';
import descriptors from '../../../../locales/en-US/descriptors';
import axisTickPositioner, {
  getEndTickPosition,
  getEndTickPositionForSingleDay,
  getStartTickPosition
} from '../../../shared/Utils/axisTickPositioner';
import { determineInterval } from '../../../shared/Utils/determineInterval';
import { getApplicableLabel } from '../../../shared/Utils/labelFormatter';
import { config } from '../constants';
import {
  IArraySeries,
  IEnergyOptimizationChartDataProps
} from '../EnergyOptimizationInterfaces';
import EnergyOptimizationChartTooltip from './EnergyOptimizationChartTooltip';
import { getLegendItems } from './EnergyOptimizationLegendConfigGenerator';
import { getYAxisLabel } from '../../../shared/Utils/PortfolioEnergy/PortfolioEnergyVDUtils';
import { symbolObj } from '../../../shared/Utils/Utils';
const d = descriptors.energyManagement;

export interface IEnergyOptimizationChartProps {
  chartData: IEnergyOptimizationChartDataProps;
  height?: string | number;
  showComfortData?: boolean;
  currencyName?: string;
  startTime?: string;
  endTime?: string;
  marginLeft?: number;
  marginRight?: number;
  showInactiveDaysData?: boolean;
  showExcludedDaysData?: boolean;
  mainGraph?: boolean;
  measurementUnit?: string;
  isConsumptionGraph?: boolean;
  isAllUtility?: boolean;
}

const EnergyOptimizationChart = ({
  chartData,
  height,
  showComfortData,
  currencyName = config.defaultCurrencyName,
  startTime,
  endTime,
  marginLeft,
  marginRight,
  showInactiveDaysData,
  showExcludedDaysData,
  mainGraph,
  isConsumptionGraph,
  measurementUnit,
  isAllUtility
}: IEnergyOptimizationChartProps) => {
  const [t, lng] = useTranslation();
  const axisMin = getStartTickPosition(startTime, chartData.actualConsumption);
  let axisMax = getEndTickPosition(
    endTime,
    chartData.actualConsumption,
    isAllUtility
  );

  if (moment(startTime).isSame(moment(endTime), 'day')) {
    axisMax = getEndTickPositionForSingleDay(
      endTime,
      chartData.actualConsumption
    );
  }

  let numOfDays = -1;
  if (axisMin !== undefined && axisMax !== undefined) {
    numOfDays = moment(axisMax).diff(axisMin, 'days');
  }

  let tickAmountValue = 5;
  if (mainGraph) {
    const digitFour = 4;
    tickAmountValue = digitFour;
  }

  const interval = determineInterval(numOfDays);
  const reSizerProps: ResizerProps = {
    refreshRate: 100,
    refreshMode: 'debounce',
    handleHeight: false
  };

  const axisFormatter: AxisTickPositionerCallbackFunction = function(
    this: Axis
  ) {
    return axisTickPositioner(
      interval,
      moment(axisMin),
      moment(axisMax),
      // @ts-ignore
      this.len
    );
  };

  let siteComfortValue: IArraySeries | undefined = undefined;
  if (showComfortData) {
    siteComfortValue = chartData.siteComfort;
  }

  return (
    <div className="energy-optimization-chart">
      <Chart
        chartType="timeseries"
        areas={{ stacking: 'normal', marker: { enabled: false } }}
        height={height}
        marginLeft={marginLeft}
        marginRight={marginRight}
        ResizerProps={reSizerProps}
      >
        <Chart.Area
          className="savings"
          lineWidth={2}
          name={d.CALCULATED.defaultMessage}
          // @ts-ignore
          data={chartData.energySavings}
          stickyTracking={false}
          trackByArea={true}
          yAxis={0}
        />
        <Chart.Area
          className="actual"
          lineWidth={0}
          name={d.ACTUAL.defaultMessage}
          // @ts-ignore
          data={chartData.actualConsumption}
          stickyTracking={false}
          trackByArea={true}
          pointStart={0}
          yAxis={0}
        />

        <Chart.ScatterPlot
          name={d.inactiveDays.defaultMessage}
          data={chartData.inactiveDays}
          className="inactive"
          marker={{
            symbol: 'circle',
            radius: 4,
            lineWidth: 1
          }}
          visible={showInactiveDaysData}
          yAxis={0}
        />

        <Chart.ScatterPlot
          name={d.excludedDays.defaultMessage}
          data={chartData.excludedDays}
          className="excluded"
          marker={{
            symbol: 'square',
            radius: 4,
            lineWidth: 1
          }}
          visible={showExcludedDaysData}
          yAxis={0}
        />

        <Chart.Line
          name={d.siteComfort.defaultMessage}
          // @ts-ignore
          data={chartData.siteComfort}
          dashStyle={'ShortDot'}
          className="comfort"
          marker={{
            enabled: false
          }}
          stickyTracking={false}
          yAxis={1}
          visible={showComfortData}
          connectNulls={true}
        />
        <Chart.XAxis
          labels={{
            formatter() {
              return getApplicableLabel(
                this.isFirst,
                this.value,
                interval,
                lng
              );
            },
            enabled:
              chartData.actualConsumption !== undefined &&
              chartData.actualConsumption.length > 0
          }}
          min={axisMin}
          max={axisMax}
          showGridLines={false}
          tickWidth={0}
          margin={0}
          maxPadding={0}
          minPadding={0}
          tickPositioner={axisFormatter}
        />
        <Chart.YAxis
          gridLineWidth={1}
          showGridLines={true}
          labels={{
            formatter() {
              // Note: chart data prop for consumption charts is already scaled. We can just render directly
              if (isConsumptionGraph)
                return getYAxisLabel(measurementUnit!, this.value);
              const value = formatters.number.currency(currencyName)(
                this.value
              );

              const modifiedValue = value;
              if (
                symbolObj[
                  modifiedValue
                    .split('')
                    .splice(0, 3)
                    .join('')
                ]
              ) {
                return (
                  symbolObj[
                    modifiedValue
                      .split('')
                      .splice(0, 3)
                      .join('')
                  ] +
                  ' ' +
                  value
                    .split('')
                    .splice(3)
                    .join('')
                );
              } else if (
                symbolObj[
                  modifiedValue
                    .split('')
                    .splice(0, 2)
                    .join('')
                ]
              ) {
                return (
                  symbolObj[
                    modifiedValue
                      .split('')
                      .splice(0, 2)
                      .join('')
                  ] +
                  ' ' +
                  value
                    .split('')
                    .splice(2)
                    .join('')
                );
              }
              return formatters.number.currency(currencyName)(this.value);
            }
          }}
          tickAmount={tickAmountValue}
        />
        <Chart.YAxis
          labelSuffix="%"
          min={0}
          max={100}
          gridLineWidth={1}
          showGridLines={false}
          opposite={true}
          showEmpty={false}
        />
        <Chart.Legend enabled={false} />
        <Chart.Tooltip
          shared={false}
          headerFormat=""
          renderContent={(details) => (
            <EnergyOptimizationChartTooltip
              actual={{
                data: chartData.actualConsumption
              }}
              eoStatus={{
                data: chartData.runTimeEOStatus
              }}
              inactive={{
                data: chartData.inactiveDays
              }}
              excluded={{ data: chartData.excludedDays }}
              savings={{
                data: chartData.energySavings
              }}
              siteComfort={{
                data: siteComfortValue
              }}
              activeHours={{
                data: chartData.activeHours
              }}
              xValue={details.x}
              currencyName={currencyName}
              showComfortData={showComfortData}
              t={t}
              lng={lng}
              isConsumption={isConsumptionGraph}
              measurementUnit={measurementUnit}
              isAllUtility={isAllUtility}
            />
          )}
          borderRadius={10}
        />
      </Chart>
      <Legend
        items={getLegendItems(
          t,
          true,
          true,
          true,
          showComfortData,
          showInactiveDaysData,
          showExcludedDaysData
        )}
        alignment="center"
      />
    </div>
  );
};

export default EnergyOptimizationChart;
